/**
 * Onboarding Store
 *
 * Zustand-based store for the first-entry onboarding wizard (invoicing
 * integration + billing entity) with command-driven architecture.
 *
 * Usage in components (registered once at the composition root — Layout):
 * ```typescript
 * import {
 *   useOnboardingSelector,
 *   useOnboardingDispatch,
 *   useOnboardingPolicies,
 *   createOnboardingPolicies,
 *   selectors
 * } from '@domains/onboarding';
 * import useBackend from '@contexts/useBackend/useBackend';
 *
 * function OnboardingRoot() {
 *   const { backend } = useBackend();
 *   const dispatch = useOnboardingDispatch();
 *   const getConnectors = useIntegrationsSelector( selectors.invoicingConnectors );
 *
 *   useOnboardingPolicies(
 *     useMemo(
 *       () => backend ? createOnboardingPolicies( backend, () => getConnectors ) : [],
 *       [backend, getConnectors]
 *     )
 *   );
 * }
 * ```
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ConnectorEntry } from '@domains/integrations';
import useAuth from '@contexts/useAuth/useAuth';
import { initialState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { OnboardingCommand } from './commands';
import { createOnboardingInitPolicy } from './policies/onboardingInitPolicy';
import { createOnboardingSubmitPolicy } from './policies/onboardingSubmitPolicy';

// Create the store (no policies passed here - they need backend / connectors)
const {
  useSelector,
  useErrors,
  usePolicies,
  getState,
  dispatch
} = createStore({
  name: 'onboarding',
  initialState,
  handlers,
  invariants
});

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them. The engine core stays
 * auth-agnostic; this web-app boundary supplies the actor. An explicit
 * `meta.user` (e.g. from tests) is left untouched. Mirrors `useAccountDispatch`.
 */
const useDispatch = (): Dispatch<OnboardingCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<OnboardingCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Resolves the invoicing connector catalog as a `Record<connectorId, ConnectorEntry>`.
 * Supplied by the composition root (Layout) from the integrations/shared store
 * selector, so the submit policy can branch via `resolveSetupPanelKind` without
 * coupling the store layer to the integrations store.
 */
export type GetConnectors = () => Record<string, ConnectorEntry>;

/**
 * Policy factory function.
 * Creates all onboarding policies with their runtime dependencies.
 *
 * @param backend       Backend instance from useBackend hook.
 * @param getConnectors Resolves the invoicing connector catalog (from the
 *                      integrations/shared store) for the submit policy's
 *                      connector-kind branching.
 * @returns Array of policies to pass to useOnboardingPolicies.
 */
export const createOnboardingPolicies = (
  backend: ClientLibraryType,
  getConnectors: GetConnectors
) => [
  createOnboardingInitPolicy( backend ),
  createOnboardingSubmitPolicy( backend, getConnectors )
];

// Export hooks with domain-specific names
export {
  useSelector as useOnboardingSelector,
  useErrors as useOnboardingErrors,
  useDispatch as useOnboardingDispatch,
  usePolicies as useOnboardingPolicies,
  getState as getOnboardingState,
  dispatch as dispatchOnboardingCommand
};

/**
 * Re-export selectors for convenience.
 */
export { selectors } from './state';
